/**
 * Friendly API error normalizer.
 *
 * Guards the UI from ever surfacing raw infrastructure strings like
 * "Not Found", "Internal Server Error", "Bad Gateway", "[object Object]",
 * "undefined", or an empty body — while still preserving genuinely useful
 * backend messages such as "CV text missing", "Access denied", etc.
 *
 * Usage:
 *   try { ... }
 *   catch (e) { setError(humanizeApiError(e, { context: 'cover-letter' })); }
 */

// Raw strings that must NEVER be shown to a user. Replace with generic copy.
const RAW_NOISE = new Set([
  '',
  'not found',
  'internal server error',
  'bad gateway',
  'gateway timeout',
  'service unavailable',
  'undefined',
  'null',
  '[object object]',
  'fetch failed',
  'failed to fetch',
  'network error',
  'network request failed',
  'unknown error',
  'true',
  'false',
]);

// Context → fallback message map
const FALLBACKS = {
  'cover-letter':
    'We could not generate your cover letter right now. Please try again in a moment.',
  'tailored-cv':
    'We could not generate your tailored CV right now. Please try again in a moment.',
  'cv-preview':
    'We could not load the CV preview right now. Please try again in a moment.',
  'cv-export':
    'We could not prepare your CV for download right now. Please try again in a moment.',
  'editor-save':
    'We could not save your changes right now. Please try again in a moment.',
  'editor-load':
    'We could not load the editor right now. Please refresh and try again.',
  'find-jobs-submit':
    'We could not submit your profile right now. Please try again.',
  default:
    'Something went wrong. Please try again in a moment.',
};

const MESSAGE_KEYS = [
  'detail',
  'message',
  'error',
  'description',
  'title',
  'statusText',
  'reason',
];

function fallbackFor(context) {
  return FALLBACKS[context] || FALLBACKS.default;
}

function isNoise(value) {
  return RAW_NOISE.has(String(value || '').trim().toLowerCase());
}

function normalizePrimitive(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/**
 * Extract the best human string out of whatever the API layer threw.
 * Handles nested FastAPI/Supabase/Axios shapes without ever returning objects.
 */
function extractRaw(value, seen = new WeakSet()) {
  const primitive = normalizePrimitive(value);
  if (primitive) return primitive;

  if (value == null) return '';

  if (value instanceof Error) {
    return (
      extractRaw(value.response?.data, seen) ||
      extractRaw(value.payload, seen) ||
      extractRaw(value.cause, seen) ||
      normalizePrimitive(value.message)
    );
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => extractRaw(item, seen))
      .map((item) => item.trim())
      .filter((item) => item && !isNoise(item))
      .join('; ');
  }

  if (typeof value === 'object') {
    if (seen.has(value)) return '';
    seen.add(value);

    for (const key of MESSAGE_KEYS) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const candidate = extractRaw(value[key], seen);
        if (candidate && !isNoise(candidate)) return candidate;
      }
    }

    // Pydantic/FastAPI validation details often arrive as arrays of objects,
    // but some backends use keyed objects. Try values as a final safe pass.
    const nestedCandidate = Object.entries(value)
      .filter(([key]) => !['success', 'code', 'ok', 'status'].includes(String(key).toLowerCase()))
      .map(([, item]) => extractRaw(item, seen))
      .map((item) => item.trim())
      .find((item) => item && !isNoise(item));
    return nestedCandidate || '';
  }

  return String(value || '').trim();
}

/**
 * @param {unknown} err           Error / response / string
 * @param {object} [opts]
 * @param {string} [opts.context] One of FALLBACKS keys
 * @returns {string}               Safe, human-facing message
 */
export function humanizeApiError(err, opts = {}) {
  const raw = String(extractRaw(err) || '').trim();
  const normalized = raw.toLowerCase();

  // Known noise → fallback
  if (RAW_NOISE.has(normalized)) {
    return FALLBACKS[opts.context] || FALLBACKS.default;
  }

  // Keep genuinely informative messages (backend-authored). They typically
  // contain spaces + more than a couple words and are not HTTP status text.
  //  e.g. "CV text missing for this submission", "Access denied",
  //       "Invalid submission_id", "Cover letter service is currently unavailable..."
  return raw;
}

export function getSafeErrorMessage(value, opts = {}) {
  return humanizeApiError(value, opts);
}

export function toSafeDisplayString(value, opts = {}) {
  const text = humanizeApiError(value, opts);
  return text && !isNoise(text) ? text : fallbackFor(opts.context);
}

export default humanizeApiError;
